import { createAction, createReducer } from "@reduxjs/toolkit"
import { api } from "./helpers/api"
import { LOADING_INITIAL_STATE, setFailed, setReceived, setRequested } from "modules/loader-watchers/helpers/loading"
import { mapPlatformFeeInvoice } from "./powertranzApi"

// ============ GET PLATFORM FEE ============
const requested = createAction("platformFees/requested")
const received = createAction("platformFees/received")
const failed = createAction("platformFees/failed")

// ============ SAVE PLATFORM FEE (PATCH) ============
const requestedSave = createAction("platformFees/requestedSave")
const receivedSave = createAction("platformFees/receivedSave")
const failedSave = createAction("platformFees/failedSave")

// ============ PLATFORM FEE INVOICES (PowerTranz guides, DS-4717) ============
const requestedInvoices = createAction("platformFees/requestedInvoices")
const receivedInvoices = createAction("platformFees/receivedInvoices")
const failedInvoices = createAction("platformFees/failedInvoices")

const requestedInvoicePdf = createAction("platformFees/requestedInvoicePdf")
const receivedInvoicePdf = createAction("platformFees/receivedInvoicePdf")
const failedInvoicePdf = createAction("platformFees/failedInvoicePdf")

// ============ INITIAL STATE ============
const initialState = {
  ...LOADING_INITIAL_STATE,
  data: null,
  save: {
    ...LOADING_INITIAL_STATE
  },
  invoices: {
    ...LOADING_INITIAL_STATE,
    items: [],
    // Set only if the API sends pagination meta alongside the invoices.
    meta: null
  },
  invoicePdf: {
    ...LOADING_INITIAL_STATE
  }
}

// ============ REDUCER ============
const platformFeesReducer = createReducer(initialState, {
  [requested.type]: (state) => {
    setRequested(state)
  },
  [received.type]: (state, { payload }) => {
    setReceived(state)
    state.data = payload.data ?? payload
  },
  [failed.type]: (state) => {
    setFailed(state)
  },

  [requestedSave.type]: (state) => {
    setRequested(state.save)
  },
  [receivedSave.type]: (state, { payload }) => {
    setReceived(state.save)
    state.data = payload.data ?? payload
  },
  [failedSave.type]: (state) => {
    setFailed(state.save)
  },

  [requestedInvoices.type]: (state) => {
    setRequested(state.invoices)
  },
  [receivedInvoices.type]: (state, { payload }) => {
    setReceived(state.invoices)
    state.invoices.items = (payload?.invoices || []).map(mapPlatformFeeInvoice)
    state.invoices.meta = payload?.meta || null
  },
  [failedInvoices.type]: (state) => {
    setFailed(state.invoices)
  },

  [requestedInvoicePdf.type]: (state) => {
    setRequested(state.invoicePdf)
  },
  [receivedInvoicePdf.type]: (state) => {
    setReceived(state.invoicePdf)
  },
  [failedInvoicePdf.type]: (state) => {
    setFailed(state.invoicePdf)
  }
})

export default platformFeesReducer

// ============ API ACTIONS ============

export const getPlatformFee = () =>
  api({
    url: "/guide/settings/platform_fee",
    method: "get",
    onStart: requested.type,
    onSuccess: received.type,
    onError: failed.type
  })

export const savePlatformFee = (data) =>
  api({
    url: "/guide/settings/platform_fee",
    method: "patch",
    data: { platform_fee: data },
    onStart: requestedSave.type,
    onSuccess: receivedSave.type,
    onError: failedSave.type,
    showSuccessNotification: true
  })

export const getPlatformFeeInvoices = (params) =>
  api({
    url: "/guide/platform_fees",
    params,
    onStart: requestedInvoices.type,
    onSuccess: receivedInvoices.type,
    onError: failedInvoices.type
  })

// Resolves to the invoice PDF as a Blob.
export const downloadPlatformFeeInvoicePdf = (id) =>
  api({
    url: `/guide/platform_fees/${id}/pdf`,
    responseType: "blob",
    onStart: requestedInvoicePdf.type,
    onSuccess: receivedInvoicePdf.type,
    onError: failedInvoicePdf.type
  })
